import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';

import { AppState } from '@cpd-app/state';
import { InstitutionsForOccupationOptions } from '@cpd-app/core-module/models';
import { QueryInstitutionsByProgramArgs } from '@cpd-app/generated';

import * as InstitutionActions from './institution.actions';
import * as InstitutionSelectors from './institution.selectors';

@Injectable({
  providedIn: 'root'
})
export class InstitutionFacade {
  public readonly institutions$ = this.store.pipe(
    select(InstitutionSelectors.selectInstitutions)
  );

  public readonly institutionsLoading$ = this.store.pipe(
    select(InstitutionSelectors.selectInstitutionsLoading)
  );

  public readonly institutionsForOccupationOptions$ = this.store.pipe(
    select(InstitutionSelectors.selectInstitutionsForOccupationOptions)
  );
  public readonly selectedInstitutionDetails$ = this.store.pipe(
    select(InstitutionSelectors.selectSelectedInstitutionDetails)
  );

  /**
   * Which school the details in the store belong to.
   *
   * Needed because the reducer keeps the previous school's details in place
   * while the next one loads, so a consumer that reads the details alone can
   * show one school's figures under another school's name.
   */
  public readonly selectedInstitutionId$ = this.store.pipe(
    select(InstitutionSelectors.selectSelectedInstitutionId)
  );

  readonly institutionsByProgram$ = this.store.pipe(
    select(InstitutionSelectors.selectInstitutionsByProgram)
  );

  /** OC-2. The size of the whole matched set, not the page in hand. */
  public readonly institutionsTotalCount$ = this.store.pipe(
    select(InstitutionSelectors.selectInstitutionsTotalCount)
  );

  /** OC-2. Where the current page starts and how many rows it holds. */
  public readonly institutionsPage$ = this.store.pipe(
    select(InstitutionSelectors.selectInstitutionsPage)
  );

  public readonly institutionsLoadedSuccessType =
    InstitutionActions.institutionsLoadedSuccess.type;

  constructor(private store: Store<AppState>) {}

  /** OC-2. Move the window, keeping every filter the member has set. */
  public setInstitutionsPage(offset: number): void {
    this.store.dispatch(InstitutionActions.setInstitutionsPage({ offset }));
  }

  public updateInstitutionsForOccupationOptions(
    options: InstitutionsForOccupationOptions
  ): void {
    this.store.dispatch(
      InstitutionActions.updateInstitutionsForOccupationOptions({ options })
    );
  }

  public clearInstitutionsForOccupationOptions(): void {
    this.store.dispatch(
      InstitutionActions.clearInstitutionsForOccupationOptions()
    );
  }

  public setSelectedInstitution(institutionId: string): void {
    this.store.dispatch(
      InstitutionActions.setSelectedInstitution({ institutionId })
    );
  }

  public loadInstitutionsByProgram(
    input: Partial<QueryInstitutionsByProgramArgs>
  ): void {
    this.store.dispatch(
      InstitutionActions.loadInstitutionsByProgram({ input })
    );
  }
}
